import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiCheck, FiX, FiPlus, FiTrash2,
  FiFileText, FiUpload, FiList,
} from 'react-icons/fi';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Payment notes hold the user's text plus server-written audit lines
// ("[Edited …]" / "[Cancelled …]"). Show them separately; never let users edit audit lines.
const AUDIT_LINE_RE = /^\[(Edited|Cancelled) .*\]$/;
const splitPaymentNotes = (notes) => {
  const lines = String(notes || '').split('\n');
  return {
    user:  lines.filter(l => !AUDIT_LINE_RE.test(l.trim())).join('\n').trim(),
    audit: lines.map(l => l.trim()).filter(l => AUDIT_LINE_RE.test(l)),
  };
};

const TYPE_LABEL = { MATERIAL: 'Material', OUTSOURCING: 'Outsourcing', OTHER: 'Other' };
const TYPE_BADGE = {
  MATERIAL:    { background: '#eff6ff', color: '#2563eb' },
  OUTSOURCING: { background: '#faf5ff', color: '#7c3aed' },
  OTHER:       { background: '#f3f4f6', color: '#6b7280' },
};
const STATUS_BADGE = {
  DRAFT:     { background: '#fef3c7', color: '#d97706' },
  CONFIRMED: { background: '#dcfce7', color: '#16a34a' },
  CANCELLED: { background: '#fee2e2', color: '#dc2626' },
};
const PAY_BADGE = {
  UNPAID:         { background: '#fee2e2', color: '#dc2626' },
  PARTIALLY_PAID: { background: '#fef3c7', color: '#d97706' },
  PAID:           { background: '#dcfce7', color: '#16a34a' },
};
const PAY_LABEL   = { UNPAID: 'Unpaid', PARTIALLY_PAID: 'Partially Paid', PAID: 'Paid' };
const METHOD_LABEL = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };

/* Resolve a stored file path (may be a bare /uploads/… path) to a full URL
   served by the backend — same approach as BillDetail. */
const API_ORIGIN = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL.replace('/api', '');
  if (window.location.hostname === 'localhost') return 'http://localhost:5000';
  const host = window.location.hostname.replace(/^www\./, '');
  return `https://api.${host}`;
})();
const resolveFileUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_ORIGIN}${url}`;
};

const btnBase = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
  fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none',
};
const btnNeutral = { ...btnBase, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151' };

/* ── ALLOCATION-BASED MODAL — COMMENTED OUT ─────────────────────
   Uncomment this component (and its usage below) to restore the
   two-step allocate-existing-payment flow.

function AllocateModal({ purchaseId, vendorId, outstanding, onClose, onSaved }) {
  const [payments,  setPayments]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [paymentId, setPaymentId] = useState('');
  const [amount,    setAmount]    = useState('');
  const [saving,    setSaving]    = useState(false);

  useEffect(() => {
    API.get(`/purchases/vendor/${vendorId}/unallocated-payments`)
      .then(({ data }) => setPayments(data.payments || []))
      .catch(() => toast.error('Failed to load payments'))
      .finally(() => setLoading(false));
  }, [vendorId]);

  const selected = payments.find(p => String(p.id) === String(paymentId));

  const handleSave = async () => {
    if (!paymentId) return toast.error('Select a payment');
    if (!Number(amount) || Number(amount) <= 0) return toast.error('Enter a valid amount');
    try {
      setSaving(true);
      await API.post('/purchases/allocate', {
        vendor_payment_id: Number(paymentId),
        purchase_id:       Number(purchaseId),
        allocated_amount:  Number(amount),
      });
      toast.success('Payment allocated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to allocate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 480, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Allocate Payment</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={18} /></button>
        </div>

        <div style={{ background: '#fef3c7', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13 }}>
          Purchase outstanding: <strong>{money(outstanding)}</strong>
        </div>

        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>Loading payments…</div>
        ) : payments.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>
            No unallocated payments available for this vendor.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Select Payment *</label>
              <select
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }}
                value={paymentId}
                onChange={e => { setPaymentId(e.target.value); setAmount(''); }}
              >
                <option value="">— Select —</option>
                {payments.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.payment_number} — {fmt(p.payment_date)} — Unallocated: ₹{Number(p.unallocated).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </option>
                ))}
              </select>
              {selected && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                  Payment total: {money(selected.amount)} &nbsp;|&nbsp; Unallocated: {money(selected.unallocated)}
                </div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Amount to Allocate (₹) *</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={Math.min(Number(selected?.unallocated || 0), Number(outstanding))}
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
              />
              {selected && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
                  Max: {money(Math.min(Number(selected.unallocated), Number(outstanding)))}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button className="crm-btn" onClick={onClose}>Cancel</button>
          {payments.length > 0 && (
            <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Allocate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
END OF ALLOCATION MODAL */

/* ── Direct Payment Modal ───────────────────────────────────────── */
function AddPaymentModal({ purchaseId, outstanding, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    amount:           String(Number(outstanding).toFixed(2)),
    payment_date:     today,
    payment_method:   'cash',
    bank_account_id:  '',
    reference_number: '',
    notes:            '',
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  const filteredAccounts = bankAccounts.filter(a => {
    if (form.payment_method === 'cash')         return a.account_type === 'cash';
    if (form.payment_method === 'upi')          return a.account_type === 'upi';
    if (form.payment_method === 'bank_transfer' || form.payment_method === 'cheque') return a.account_type === 'bank';
    return true;
  });

  const handleSave = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    if (amt > Number(outstanding) + 0.001) {
      return toast.error(`Amount cannot exceed outstanding balance ${money(outstanding)}`);
    }
    try {
      setSaving(true);
      const { data } = await API.post(`/purchases/${purchaseId}/direct-payment`, {
        amount:           amt,
        payment_date:     form.payment_date,
        payment_method:   form.payment_method,
        bank_account_id:  form.bank_account_id || null,
        reference_number: form.reference_number || null,
        notes:            form.notes || null,
      });
      toast.success(`Payment recorded — ${data.payment_number}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 500, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Add Payment</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={18} /></button>
        </div>

        {/* Balance info */}
        <div style={{ display: 'flex', gap: 16, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 14px', marginBottom: 18, fontSize: 13 }}>
          <span style={{ color: '#92400e' }}>Outstanding: <strong>{money(outstanding)}</strong></span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Amount (₹) *</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Payment Date *</label>
            <input
              type="date"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}
              value={form.payment_date}
              onChange={e => set('payment_date', e.target.value)}
              onClick={e => e.currentTarget.showPicker?.()}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Payment Method</label>
            <select
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }}
              value={form.payment_method}
              onChange={e => set('payment_method', e.target.value) || set('bank_account_id', '')}
            >
              {Object.entries(METHOD_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Reference / UTR No.</label>
            <input
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
              value={form.reference_number}
              onChange={e => set('reference_number', e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Paid From Account</label>
            <select
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }}
              value={form.bank_account_id}
              onChange={e => set('bank_account_id', e.target.value)}
            >
              <option value="">— Select Account —</option>
              {filteredAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Notes</label>
            <textarea
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="crm-btn" onClick={onClose}>Cancel</button>
          <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Payment Modal (admin only) ─────────────────────────────── */
function EditPaymentModal({ purchaseId, alloc, outstanding, onClose, onSaved }) {
  const pay        = alloc.payment || {};
  const maxAmount  = Number(outstanding || 0) + Number(alloc.allocated_amount || 0);
  const [form, setForm] = useState({
    amount:           String(Number(alloc.allocated_amount).toFixed(2)),
    payment_date:     String(pay.payment_date || '').slice(0, 10),
    payment_method:   pay.payment_method || 'cash',
    bank_account_id:  pay.bank_account_id ? String(pay.bank_account_id) : '',
    reference_number: pay.reference_number || '',
    notes:            splitPaymentNotes(pay.notes).user,
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  const filteredAccounts = bankAccounts.filter(a => {
    if (form.payment_method === 'cash')         return a.account_type === 'cash';
    if (form.payment_method === 'upi')          return a.account_type === 'upi';
    if (form.payment_method === 'bank_transfer' || form.payment_method === 'cheque') return a.account_type === 'bank';
    return true;
  });
  // Keep the payment's current account selectable even if it is inactive / filtered out
  const currentAcc = pay.bankAccount;
  const showCurrentExtra = currentAcc && form.bank_account_id === String(currentAcc.id)
    && !filteredAccounts.some(a => a.id === currentAcc.id);

  const handleSave = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    if (amt > maxAmount + 0.001) return toast.error(`Amount cannot exceed ${money(maxAmount)}`);
    if (!form.payment_date) return toast.error('Payment date is required');
    try {
      setSaving(true);
      const { data } = await API.put(`/purchases/${purchaseId}/payments/${alloc.id}`, {
        amount:           amt,
        payment_date:     form.payment_date,
        payment_method:   form.payment_method,
        bank_account_id:  form.bank_account_id || null,
        reference_number: form.reference_number || null,
        notes:            form.notes || null,
      });
      if (data.unchanged) {
        toast('No changes to save');
      } else {
        toast.success(`Payment ${data.payment_number} updated`);
        (data.warnings || []).forEach(w => toast(w, { icon: '⚠️' }));
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
  const inp = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 500, maxWidth: '95vw', maxHeight: '95vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            Edit Payment {pay.payment_number && <span style={{ marginLeft: 8, padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: '#ede9fe', color: '#6d28d9' }}>{pay.payment_number}</span>}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={18} /></button>
        </div>

        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 14px', marginBottom: 18, fontSize: 13, color: '#1e40af' }}>
          Maximum amount for this payment: <strong>{money(maxAmount)}</strong>. Changes update the vendor ledger and account ledger, and every edit is logged in the payment notes.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={lbl}>Amount (₹) *</label>
            <input type="number" min="0.01" step="0.01" style={inp} value={form.amount} onChange={e => set('amount', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Payment Date *</label>
            <input
              type="date"
              style={{ ...inp, cursor: 'pointer' }}
              value={form.payment_date}
              onChange={e => set('payment_date', e.target.value)}
              onClick={e => e.currentTarget.showPicker?.()}
            />
          </div>
          <div>
            <label style={lbl}>Payment Method</label>
            <select
              style={{ ...inp, background: '#fff' }}
              value={form.payment_method}
              onChange={e => setForm(f => ({ ...f, payment_method: e.target.value, bank_account_id: '' }))}
            >
              {Object.entries(METHOD_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Reference / UTR No.</label>
            <input style={inp} value={form.reference_number} onChange={e => set('reference_number', e.target.value)} placeholder="Optional" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Paid From Account</label>
            <select style={{ ...inp, background: '#fff' }} value={form.bank_account_id} onChange={e => set('bank_account_id', e.target.value)}>
              <option value="">— Select Account —</option>
              {showCurrentExtra && <option value={currentAcc.id}>{currentAcc.account_name}</option>}
              {filteredAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Notes</label>
            <textarea
              style={{ ...inp, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="crm-btn" onClick={onClose}>Cancel</button>
          <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Upload / Replace Bill PDF Modal ────────────────────────────── */
function PdfUploadModal({ purchase, existingDocs, onClose, onSaved }) {
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const isReplace = existingDocs.length > 0;

  const handleUpload = async () => {
    if (!file) return toast.error('Select a file');
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const { data: up } = await API.post('/upload/purchase-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await API.post(`/purchases/${purchase.id}/documents`, {
        document_type: 'vendor_invoice',
        document_name: file.name,
        file_path:     up.url,
      });
      // Replace: remove previous document(s) once the new one is saved (best-effort)
      await Promise.all(
        existingDocs.map(d =>
          API.delete(`/purchases/${purchase.id}/documents/${d.id}`).catch(() => null)
        )
      );
      toast.success(isReplace ? 'PDF replaced' : 'PDF uploaded');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{isReplace ? 'Replace Bill PDF' : 'Upload Bill PDF'}</h3>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          {isReplace ? 'The current file will be replaced for ' : 'Attach the vendor bill for '}
          <strong>{purchase.purchase_number}</strong>.
        </div>
        <div
          onClick={() => fileRef.current?.click()}
          style={{ border: '2px dashed #d1d5db', borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: 'pointer', background: '#fafafa', marginBottom: 16 }}
        >
          <FiUpload size={24} style={{ color: '#9ca3af', marginBottom: 8 }} />
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {file ? <strong style={{ color: '#111' }}>{file.name}</strong> : 'Click to select file (PDF, PNG, JPG)'}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0] || null)}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--primary, #2563eb)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: uploading || !file ? 0.6 : 1 }}
          >
            {uploading ? 'Uploading…' : isReplace ? 'Replace PDF' : 'Upload PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */
export default function AdminPurchaseDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin'; // only admins may delete payments

  const [purchase,       setPurchase]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [paymentModal,   setPaymentModal]   = useState(false);
  const [editAlloc,      setEditAlloc]      = useState(null); // allocation being edited
  const [pdfModal,       setPdfModal]       = useState(false);
  const [showHistory,    setShowHistory]    = useState(false);
  const [acting,         setActing]         = useState(false);

  // const [allocateModal, setAllocateModal] = useState(false); // ALLOCATION: uncomment to restore

  const fetchPurchase = useCallback(async () => {
    try {
      const { data } = await API.get(`/purchases/${id}`);
      setPurchase(data.purchase);
    } catch {
      toast.error('Failed to load purchase');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchPurchase(); }, [fetchPurchase]);

  const handleConfirm = async () => {
    if (!window.confirm('Confirm this purchase? Financial fields will be locked.')) return;
    try {
      setActing(true);
      await API.post(`/purchases/${id}/confirm`);
      toast.success('Purchase confirmed');
      fetchPurchase();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this purchase? This cannot be undone.')) return;
    try {
      setActing(true);
      await API.post(`/purchases/${id}/cancel`);
      toast.success('Purchase cancelled');
      fetchPurchase();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setActing(false);
    }
  };

  const handleRemovePayment = async (alloc) => {
    const no = alloc.payment?.payment_number || 'this payment';
    if (!window.confirm(
      `Delete payment ${no} (${money(alloc.allocated_amount)})?\n\n` +
      'The payment will be cancelled: it will be removed from the vendor ledger and bank balance, ' +
      'and this purchase will show the amount as unpaid again.'
    )) return;
    try {
      setActing(true);
      await API.delete(`/purchases/${id}/payments/${alloc.id}`);
      toast.success(`Payment ${no} deleted`);
      fetchPurchase();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete payment');
    } finally {
      setActing(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;
  if (!purchase) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Purchase not found</div>;

  const p = purchase;
  const isDraft     = p.status === 'DRAFT';
  const isConfirmed = p.status === 'CONFIRMED';
  const isCancelled = p.status === 'CANCELLED';
  const allocations = p.allocations || [];
  const docs        = p.documents || [];
  // Latest uploaded document acts as "the bill PDF"
  const billDoc     = docs.length ? [...docs].sort((x, y) => Number(y.id) - Number(x.id))[0] : null;

  const SectionHeader = ({ title, action }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: '#374151' }}>{title}</div>
      {action}
    </div>
  );

  return (
    <div style={{ padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/admin/purchases')} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}>
            <FiArrowLeft size={16} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem', fontFamily: 'monospace' }}>{p.purchase_number}</h2>
              <span style={{ ...TYPE_BADGE[p.purchase_type], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                {TYPE_LABEL[p.purchase_type]}
              </span>
              <span style={{ ...STATUS_BADGE[p.status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                {p.status}
              </span>
              <span style={{ ...PAY_BADGE[p.payment_status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                {PAY_LABEL[p.payment_status]}
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>
              {p.vendor?.company_name} &nbsp;·&nbsp; {fmt(p.purchase_date)}
              {p.order?.orderNumber && (
                <> &nbsp;·&nbsp; <span style={{ color: '#6366f1', fontWeight: 600 }}>{p.order.orderNumber}</span></>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isDraft && (
            <>
              <button className="crm-btn" onClick={() => navigate(`/admin/purchases/${id}/edit`)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiEdit2 size={13} /> Edit
              </button>
              <button className="crm-btn crm-btn--primary" onClick={handleConfirm} disabled={acting} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiCheck size={13} /> Confirm
              </button>
            </>
          )}
          {isConfirmed && (
            <button className="crm-btn" onClick={() => navigate(`/admin/purchases/${id}/edit`)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiEdit2 size={13} /> Edit Due Date / Notes
            </button>
          )}
          {!isCancelled && (
            <button
              onClick={handleCancel}
              disabled={acting}
              style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FiX size={13} /> Cancel
            </button>
          )}
          {/* Bill PDF: Replace / Preview (or Upload when none) */}
          {billDoc ? (
            <>
              <button onClick={() => setPdfModal(true)} style={btnNeutral}>
                <FiUpload size={14} /> Replace PDF
              </button>
              <a
                href={resolveFileUrl(billDoc.file_path)}
                target="_blank"
                rel="noreferrer"
                style={{ ...btnBase, border: '1px solid #3b82f6', color: '#3b82f6', background: '#eff6ff' }}
              >
                <FiFileText size={14} /> Preview Bill
              </a>
            </>
          ) : (
            <button onClick={() => setPdfModal(true)} style={btnNeutral}>
              <FiUpload size={14} /> Upload PDF
            </button>
          )}
          {!isCancelled && p.outstanding > 0.001 && (
            <button
              onClick={() => setPaymentModal(true)}
              style={{ ...btnBase, border: 'none', background: 'var(--primary, #2563eb)', color: '#fff' }}
            >
              <FiPlus size={14} /> Record Payment
            </button>
          )}
          {allocations.length > 0 && (
            <button onClick={() => setShowHistory(h => !h)} style={btnNeutral}>
              <FiList size={14} /> History ({allocations.length})
            </button>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Grand Total',  value: money(p.grand_total),      color: '#374151' },
          { label: 'Paid',         value: money(p.total_allocated),  color: '#16a34a' },
          { label: 'Outstanding',  value: money(p.outstanding),      color: p.outstanding > 0 ? '#dc2626' : '#16a34a' },
          { label: 'Due Date',     value: fmt(p.due_date),           color: '#374151' },
        ].map(s => (
          <div key={s.label} className="crm-card" style={{ padding: '14px 18px' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Section: Items ── */}
      <div className="crm-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
        <SectionHeader title={`Items (${(p.items || []).length})`} />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f3f4f6', background: '#fafafa' }}>
                {['#', 'Item / Work', 'Description', 'Qty', 'Unit', 'Rate', 'Discount', 'Taxable',
                  ...(p.tax_type === 'INTRA_STATE' ? ['CGST', 'SGST'] : ['IGST']),
                  'Line Total'
                ].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(p.items || []).map((item, idx) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '11px 14px', color: '#9ca3af' }}>{idx + 1}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{item.item_name}</td>
                  <td style={{ padding: '11px 14px', color: '#6b7280', fontSize: 13 }}>{item.description || '—'}</td>
                  <td style={{ padding: '11px 14px' }}>{Number(item.quantity).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '11px 14px', color: '#6b7280' }}>{item.unit || '—'}</td>
                  <td style={{ padding: '11px 14px' }}>₹{Number(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td style={{ padding: '11px 14px', color: '#dc2626' }}>{money(item.discount_amount)}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{money(item.taxable_amount)}</td>
                  {p.tax_type === 'INTRA_STATE' ? (
                    <>
                      <td style={{ padding: '11px 14px', color: '#6b7280' }}>{money(item.cgst_amount)} <span style={{ fontSize: 11 }}>({item.cgst_rate}%)</span></td>
                      <td style={{ padding: '11px 14px', color: '#6b7280' }}>{money(item.sgst_amount)} <span style={{ fontSize: 11 }}>({item.sgst_rate}%)</span></td>
                    </>
                  ) : (
                    <td style={{ padding: '11px 14px', color: '#6b7280' }}>{money(item.igst_amount)} <span style={{ fontSize: 11 }}>({item.igst_rate}%)</span></td>
                  )}
                  <td style={{ padding: '11px 14px', fontWeight: 700 }}>{money(item.line_total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#fafafa', borderTop: '2px solid #f3f4f6' }}>
                <td colSpan={p.tax_type === 'INTRA_STATE' ? 7 : 6} style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Totals</td>
                <td style={{ padding: '10px 14px', fontWeight: 700 }}>{money(p.taxable_amount)}</td>
                {p.tax_type === 'INTRA_STATE' ? (
                  <>
                    <td style={{ padding: '10px 14px', fontWeight: 700 }}>{money(p.cgst_amount)}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700 }}>{money(p.sgst_amount)}</td>
                  </>
                ) : (
                  <td style={{ padding: '10px 14px', fontWeight: 700 }}>{money(p.igst_amount)}</td>
                )}
                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#6366f1' }}>{money(p.grand_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Payment History (toggled by the History button) ── */}
      {showHistory && allocations.length > 0 && (
        <div className="crm-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
          <SectionHeader title="Payment History" />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 620 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['#', 'Date', 'Payment No', 'Mode', 'Account', 'Amount', 'Note', ''].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allocations.map((a, i) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px', color: '#9ca3af' }}>{i + 1}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>{fmt(a.payment?.payment_date)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {a.payment?.payment_number
                        ? <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>{a.payment.payment_number}</span>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#2563eb' }}>
                        {METHOD_LABEL[a.payment?.payment_method] || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{a.payment?.bankAccount?.account_name || '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#16a34a' }}>{money(a.allocated_amount)}</td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>
                      {(() => {
                        const { user: userNotes, audit } = splitPaymentNotes(a.payment?.notes);
                        const text = [a.payment?.reference_number, userNotes].filter(Boolean).join(' · ');
                        return (
                          <>
                            {text || '—'}
                            {audit.length > 0 && (
                              <span
                                title={audit.join('\n')}
                                style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#b45309', cursor: 'help' }}
                              >
                                edited
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!isCancelled && isAdmin && (
                        <button
                          onClick={() => setEditAlloc(a)}
                          disabled={acting}
                          title="Edit payment"
                          style={{ background: '#eff6ff', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', marginRight: 6 }}
                        >
                          <FiEdit2 size={13} color="#2563eb" />
                        </button>
                      )}
                      {!isCancelled && isAdmin && (
                        <button
                          onClick={() => handleRemovePayment(a)}
                          disabled={acting}
                          title="Delete payment"
                          style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: acting ? 'not-allowed' : 'pointer', opacity: acting ? 0.6 : 1 }}
                        >
                          <FiTrash2 size={13} color="#dc2626" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Vendor Details ── */}
      {p.vendor && (
        <div className="crm-card" style={{ padding: 20, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Vendor Details</div>
          {[
            ['Company', p.vendor.company_name],
            ['Code',    p.vendor.vendor_code],
            ['Contact', p.vendor.contact_person],
            ['Phone',   [p.vendor.phone, p.vendor.alternate_phone].filter(Boolean).join(', ')],
            ['Email',   p.vendor.email],
            ['GST No.', p.vendor.gstin],
            ['PAN',     p.vendor.pan],
            ['Address', [p.vendor.address_line1, p.vendor.address_line2, p.vendor.city, p.vendor.state, p.vendor.pincode].filter(Boolean).join(', ')],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} style={{ display: 'flex', gap: 10, marginBottom: 8, fontSize: 13 }}>
              <span style={{ color: '#6b7280', minWidth: 70, fontWeight: 600 }}>{label}</span>
              <span style={{ color: '#111' }}>{value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {editAlloc && (
        <EditPaymentModal
          purchaseId={id}
          alloc={editAlloc}
          outstanding={p.outstanding}
          onClose={() => setEditAlloc(null)}
          onSaved={fetchPurchase}
        />
      )}
      {paymentModal && (
        <AddPaymentModal
          purchaseId={id}
          outstanding={p.outstanding}
          onClose={() => setPaymentModal(false)}
          onSaved={fetchPurchase}
        />
      )}
      {/* ALLOCATION: uncomment to restore old allocate modal
      {allocateModal && (
        <AllocateModal
          purchaseId={id}
          vendorId={p.vendor_id}
          outstanding={p.outstanding}
          onClose={() => setAllocateModal(false)}
          onSaved={fetchPurchase}
        />
      )} */}
      {pdfModal && (
        <PdfUploadModal
          purchase={p}
          existingDocs={docs}
          onClose={() => setPdfModal(false)}
          onSaved={fetchPurchase}
        />
      )}
    </div>
  );
}